import { useState, useEffect } from 'react';
import type { Perfil, Topico, Subtopico } from '../../../types';

interface UseBatchProcessorProps {
  mappedPerfis: Perfil[];
  selectedPerfil: Perfil | null;
  showToast: (message: string, type: 'success' | 'error') => void;
  setIsBottomOpen: (open: boolean) => void;
}

export function useBatchProcessor({
  mappedPerfis,
  selectedPerfil,
  showToast,
  setIsBottomOpen,
}: UseBatchProcessorProps) {
  // Controlled Batch Processing States
  const [globalProcStatus, setGlobalProcStatus] = useState<'idle' | 'running' | 'stopping' | 'stopped'>('idle');
  const [globalProcProgress, setGlobalProcProgress] = useState<number>(0);
  const [globalProcTimer, setGlobalProcTimer] = useState<number>(0);
  const [globalProcLogs, setGlobalProcLogs] = useState<string[]>([]);
  const [globalAutoShutdown, setGlobalAutoShutdown] = useState<boolean>(true);

  // Queue and Active Item Tracking
  const [queuedRoteiros, setQueuedRoteiros] = useState<Subtopico[]>([]);
  const [completedRoteiroIds, setCompletedRoteiroIds] = useState<string[]>([]);
  const [activeRoteiro, setActiveRoteiro] = useState<Subtopico | null>(null);

  // Iniciar processamento de lote (Batch Process)
  const startBatchProcess = (roteirosToRun: Subtopico[]) => {
    if (roteirosToRun.length === 0) {
      showToast('Nenhum roteiro disponível para processar.', 'error');
      return;
    }

    setIsBottomOpen(true);
    setGlobalProcStatus('running');
    setGlobalProcProgress(0);
    setGlobalProcTimer(0);
    setCompletedRoteiroIds([]);
    setQueuedRoteiros(roteirosToRun);
    setGlobalProcLogs([
      `[${new Date().toLocaleTimeString()}] [SYSTEM] Iniciando processamento em lote para ${roteirosToRun.length} roteiro(s)...`,
      `[${new Date().toLocaleTimeString()}] [DOCKER] Iniciando contêineres e verificando dependências...`,
      `[${new Date().toLocaleTimeString()}] [DATABASE] Conectado ao PostgreSQL local (porta 5433).`,
      `[${new Date().toLocaleTimeString()}] [REDIS] Conectado ao servidor Redis.`
    ]);
  };

  const handleStartProcessPerfil = (perfilName: string) => {
    const targetPerfil = mappedPerfis.find(p => p.nome === perfilName);
    if (!targetPerfil) return;

    const allRoteiros: Subtopico[] = [];
    targetPerfil.topicos.forEach(t => {
      (t.subtopicos || []).forEach(s => {
        allRoteiros.push(s);
      });
    });
    startBatchProcess(allRoteiros);
  };

  const handleStartProcessTopico = (topicTitle: string) => {
    if (!selectedPerfil) return;
    const targetTopico = selectedPerfil.topicos.find(t => t.titulo === topicTitle);
    if (!targetTopico) return;

    const allRoteiros = targetTopico.subtopicos || [];
    startBatchProcess(allRoteiros);
  };

  const handleStartProcessTudo = () => {
    const allRoteiros: Subtopico[] = [];
    mappedPerfis.forEach(p => {
      p.topicos.forEach(t => {
        (t.subtopicos || []).forEach(s => {
          allRoteiros.push(s);
        });
      });
    });
    startBatchProcess(allRoteiros);
  };

  // Execution interval for batch process
  useEffect(() => {
    let timerInterval: ReturnType<typeof setInterval> | null = null;
    let processInterval: ReturnType<typeof setInterval> | null = null;

    if (globalProcStatus === 'running') {
      timerInterval = setInterval(() => {
        setGlobalProcTimer(prev => prev + 1);
      }, 1000);

      let currentRoteiroIdx = 0;
      let stepInRoteiro = 0;
      const totalRoteirosCount = queuedRoteiros.length;

      if (totalRoteirosCount > 0) {
        setActiveRoteiro(queuedRoteiros[0]);

        processInterval = setInterval(() => {
          stepInRoteiro += 1;
          const roteiroObj = queuedRoteiros[currentRoteiroIdx];

          if (!roteiroObj) {
            setGlobalProcStatus('stopped');
            setActiveRoteiro(null);
            setGlobalProcLogs(prev => [
              ...prev,
              `[${new Date().toLocaleTimeString()}] [SUCCESS] Processamento concluído com sucesso para todos os roteiros!`,
              `[${new Date().toLocaleTimeString()}] [SYSTEM] Encerrando instâncias adicionais do Chrome.`
            ]);
            setGlobalProcProgress(100);
            if (processInterval) clearInterval(processInterval);
            return;
          }

          const progressPercent = Math.min(
            99,
            Math.round(
              ((currentRoteiroIdx * 10 + stepInRoteiro) / (totalRoteirosCount * 10)) * 100
            )
          );
          setGlobalProcProgress(progressPercent);

          const timeStr = new Date().toLocaleTimeString();

          // Find the parent topic name dynamically
          const parentTopic = selectedPerfil?.topicos.find(t =>
            t.subtopicos.some(s => s.id === roteiroObj.id)
          ) || selectedPerfil?.topicos[0];
          const topicName = parentTopic ? parentTopic.titulo : 'Geral';

          if (stepInRoteiro === 1) {
            setGlobalProcLogs(prev => [
              ...prev,
              `[${timeStr}] [PLAYWRIGHT] 🚀 Iniciando: Tópico "${topicName}" -> Roteiro "${roteiroObj.titulo}"...`,
              `[${timeStr}] [BROWSER] Navegando para http://localhost:3443${roteiroObj.config?.urlInicial || '/login'}`
            ]);
          } else if (stepInRoteiro === 3) {
            setGlobalProcLogs(prev => [
              ...prev,
              `[${timeStr}] [PLAYWRIGHT] [${roteiroObj.titulo}] Preenchendo credenciais e efetuando login de simulação...`
            ]);
          } else if (stepInRoteiro === 5) {
            setGlobalProcLogs(prev => [
              ...prev,
              `[${timeStr}] [TTS] Narrando roteiro "${roteiroObj.titulo}": "Módulo carregado, iniciando preenchimento do formulário..."`
            ]);
          } else if (stepInRoteiro === 8) {
            setGlobalProcLogs(prev => [
              ...prev,
              `[${timeStr}] [PDF] Compilando capturas de tela para o Manual PDF de "${roteiroObj.titulo}"...`
            ]);
          } else if (stepInRoteiro >= 10) {
            setCompletedRoteiroIds(prev => [...prev, roteiroObj.id]);
            setGlobalProcLogs(prev => [
              ...prev,
              `[${timeStr}] [SUCCESS] Roteiro "${roteiroObj.titulo}" processado com sucesso! Mídias e PDF gerados no disco.`
            ]);

            currentRoteiroIdx += 1;
            stepInRoteiro = 0;
            if (currentRoteiroIdx < totalRoteirosCount) {
              setActiveRoteiro(queuedRoteiros[currentRoteiroIdx]);
            } else {
              setGlobalProcStatus('stopped');
              setActiveRoteiro(null);
              setGlobalProcLogs(prev => [
                ...prev,
                `[${timeStr}] [SUCCESS] Processamento em lote finalizado com sucesso! [100% OK]`,
                `[${timeStr}] [DOCKER] Encerrando contêineres e liberando memória GPU...`
              ]);
              setGlobalProcProgress(100);
            }
          }
        }, 1000);
      }
    }

    return () => {
      if (timerInterval) clearInterval(timerInterval);
      if (processInterval) clearInterval(processInterval);
    };
  }, [globalProcStatus, queuedRoteiros, selectedPerfil]);

  return {
    globalProcStatus,
    setGlobalProcStatus,
    globalProcProgress,
    setGlobalProcProgress,
    globalProcTimer,
    setGlobalProcTimer,
    globalProcLogs,
    setGlobalProcLogs,
    globalAutoShutdown,
    setGlobalAutoShutdown,
    queuedRoteiros,
    completedRoteiroIds,
    activeRoteiro,
    startBatchProcess,
    handleStartProcessPerfil,
    handleStartProcessTopico,
    handleStartProcessTudo,
  };
}
